/**
 * 요청 캐시 — 같은 리소스를 두 번 조회하지 않게 한다.
 *
 * 스토어 액션들이 쓰던 "state 에 값이 있으면 건너뛴다" 방식은 두 가지를 못 막았다.
 *
 *   1) 동시 호출  state 는 응답이 도착한 뒤에 commit 되므로, 같은 tick 에 두 곳에서
 *                 dispatch 하면 둘 다 빈 state 를 보고 요청을 쏜다.
 *                 (ProjectLayout.created 와 boards/tab/Index.created 가 이 경우다)
 *   2) 단일 슬롯  companyInfo 처럼 마지막 조회 결과 하나만 담아두면, 서로 다른 id 를
 *                 번갈아 조회할 때 매번 서로를 밀어내서 캐시가 무의미해진다.
 *
 * 여기서는 id 를 키에 넣어 보관하고, 진행 중인 요청(inflight)은 같은 Promise 를
 * 돌려준다. 캐시가 맞았더라도 호출한 쪽은 값을 받으므로, 액션은 매번 commit 해서
 * store 상태를 응답과 일치시켜야 한다.
 *
 * ── 사용 예 ────────────────────────────────────────────────────────────────
 *   const data = await cached(`company:${id}`, () => HttpRequest.get(`/api/v1/company/${id}`))
 *   commit('LOAD_COMPANY', data)
 *
 * ── 키 규칙 ────────────────────────────────────────────────────────────────
 *   config:{key}          서버 설정.        로그인까지 유지
 *   member:detail:{proj}  내 정보.          로그인 / 프로필 수정 시 무효화
 *   project:{id}          프로젝트 정보.    로그인 / 프로젝트 수정 시 무효화
 *   company:{id}          회사 정보.        로그인 / 회사 정보 수정 시 무효화
 *   noti:unread           안 읽은 알림.     30 초 TTL, 읽음 처리 시 무효화
 *
 * 실패한 요청은 보관하지 않는다. 일시적인 오류를 세션 내내 물고 가지 않도록.
 */

// key -> Promise (응답을 기다리는 중)
const inflight = new Map();
// key -> { value, ts }
const settled = new Map();

/**
 * @param {String} key 캐시 키. 같은 리소스는 같은 키를 써야 공유된다.
 * @param {Function} loader 캐시가 없을 때 실행할 함수. Promise 를 돌려줘야 한다.
 * @param {Number} [ttlMs] 보관 기간(ms). 기본값은 무효화 전까지 계속 유지.
 * @returns {Promise<*>} loader 가 돌려준 값
 */
export function cached(key, loader, ttlMs) {
  const ttl = typeof ttlMs === "number" ? ttlMs : Infinity;

  const hit = settled.get(key);
  if (hit && Date.now() - hit.ts < ttl) {
    return Promise.resolve(hit.value);
  }

  const running = inflight.get(key);
  if (running) return running;

  // .finally 는 쓰지 않는다(빌드 타깃 호환). 성공/실패 양쪽에서 직접 정리한다.
  const promise = loader().then(
    (value) => {
      inflight.delete(key);
      settled.set(key, { value, ts: Date.now() });
      return value;
    },
    (err) => {
      inflight.delete(key);
      throw err;
    }
  );

  inflight.set(key, promise);
  return promise;
}

/**
 * 키 또는 접두사로 캐시를 버린다.
 *   invalidate('company:12')  해당 항목만
 *   invalidate('company:')    회사 전체
 */
export function invalidate(prefix) {
  if (!prefix) return;
  Array.from(settled.keys()).forEach((k) => {
    if (k.indexOf(prefix) === 0) settled.delete(k);
  });
  Array.from(inflight.keys()).forEach((k) => {
    if (k.indexOf(prefix) === 0) inflight.delete(k);
  });
}

/** 로그인 / 로그아웃처럼 사용자가 바뀔 때 전부 버린다. */
export function clearAll() {
  settled.clear();
  inflight.clear();
}

export default { cached, invalidate, clearAll };
